import React, { useState, useEffect } from "react";
import { useLocation} from "react-router-dom";
import {
    FileText,
    User,
    Clock,
    ArrowRightLeft,
    Users,
    Flag,
    MessageSquare,
    Calendar,
    Activity,
    Layers,
    UserCheck,
    ArrowRight
} from "lucide-react";
import Loader from "react-spinner-loader";
import "./DetailsAuditHistory.css";

// Helper component for Status Change details
const StatusChangeDetails = ({ event, formatDateTime }) => (
    <div style={{ marginTop: "0px" }}>
        {/* Escalate Remarks */}
        {event.escalateRemarks && (
            <div style={{
                backgroundColor: "#FFFBEB",
                border: "1px solid #FCD34D",
                padding: "12px",
                borderRadius: "6px",
                marginBottom: "8px"
            }}>
                <div style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#92400E",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                }}>
                    <Flag size={12} />
                    Escalate Remarks
                </div>
                <div style={{
                    fontSize: "13px",
                    color: "#78350F",
                    fontWeight: "500",
                    lineHeight: "1.4"
                }}>
                    {event.escalateRemarks}
                </div>
            </div>
        )}

    </div>
);



// Status Change Event Component - Updated
const StatusChangeEvent = ({ event, formatDateTime }) => (
    <div>
        {/* Event Header - Customizable for Status Change */}
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "10px"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                <div style={{
                    padding: "6px 12px",
                    backgroundColor: "#F8FAFC",
                    color: "#64748B",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                }}>
                    {event.type}
                </div>

                {event.previous && event.current && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "14px"
                    }}>
                        <span style={{
                            padding: "4px 8px",
                            backgroundColor: "#FEE2E2",
                            color: "#991B1B",
                            borderRadius: "4px",
                            fontWeight: "600"
                        }}>
                            {event.previous}
                        </span>
                        <ArrowRight size={16} color="#64748B" />
                        <span style={{
                            padding: "4px 8px",
                            backgroundColor: "#D1FAE5",
                            color: "#065F46",
                            borderRadius: "4px",
                            fontWeight: "600"
                        }}>
                            {event.current}
                        </span>
                    </div>
                )}
            </div>

            

            <div style={{
                fontSize: "14px",
                color: "#64748B",
                fontWeight: "500",
                whiteSpace: "nowrap"
            }}>
                <Clock size={16} style={{ marginRight: "6px" }} />
                {formatDateTime(event.timestamp)}
            </div>
        </div>

        {/* User and Level Information in a single row */}
        {!event.reassignmentData && (
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "0px",
            padding: "6px 10px",
            backgroundColor: "#F8FAFC",
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
            alignItems: "center",
            justifyContent: "space-between"
        }}>
            {/* Changed By | User Level | SLA Paused At — all in one row */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap"
            }}>
                {/* Changed By */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ fontSize: "13px", color: "#64748B" }}>Performed By:</div>
                    <div style={{
                        fontWeight: "600",
                        color: "#0F172A",
                        // backgroundColor: "#F1F5F9",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "13px"
                    }}>
                        {event.user}
                    </div>
                </div>

                {/* User Level */}
                {event.userLevel && (
                    <>
                        <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <User size={14} color="#64748B" />
                            <div style={{ fontSize: "13px", color: "#64748B" }}>User Level:</div>
                            <div style={{
                                fontWeight: "600",
                                color: "#0F172A",
                                // backgroundColor: "#F1F5F9",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "13px"
                            }}>
                                {event.userLevel}
                            </div>
                        </div>
                    </>
                )}
                
</div>
                {/* ✅ SLA Paused At — inline, only for ISSUE_PENDING_AT_EXTERNAL_DEPARTMENT */}
                {event.slaPausedAt && event.originalSaveLevel === "ISSUE_PENDING_AT_EXTERNAL_DEPARTMENT" && (
                    <>
                        <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Clock size={14} color="#C2410C" />
                            <div style={{ fontSize: "13px", color: "#64748B" }}>SLA Paused At:</div>
                            <div style={{
                                fontWeight: "700",
                                color: "#9A3412",
                                backgroundColor: "rgba(234, 88, 12, 0.1)",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "13px",
                                border: "1px solid #FED7AA"
                            }}>
                                {event.slaPausedAt}
                            </div>
                        </div>
                    </>
                )}
                {/* ✅ SLA Resumed At — Show when status changes from "Issues Pending at External Vendor" to "In-Progress" */}
                {event.slaPaused === false &&
                    event.previous === "Issues Pending at External Vendor" &&
                    event.statusChangeDateTime && (
                        <>
                            <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Clock size={14} color="#059669" />
                                <div style={{ fontSize: "13px", color: "#64748B" }}>SLA Resumed At:</div>
                                <div style={{
                                    fontWeight: "700",
                                    color: "#065F46",
                                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    border: "1px solid #A7F3D0"
                                }}>
                                    {formatDateTime(event.statusChangeDateTime)}
                                </div>
                            </div>
                        </>
                    )}
            </div>
)}
            {/* ✅ NEW: Reassignment Details - Show when reassignment data is present */}
            {event.reassignmentData && (
                <div style={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    padding: "12px",
                    borderRadius: "6px",
                    marginTop: "10px",
                    marginBottom: "8px"
                }}>
                    <div style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#64748B",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <UserCheck size={12} />
                        Reassignment Details
                    </div>

                    {/* Reassignment Info Row */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                        padding: "8px 12px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: "1px solid #E2E8F0",
                        justifyContent: "space-between"
                    }}>
                        {/* Reassigned By */}
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "12px", color: "#64748B" }}>Reassigned By:</span>
                            <span style={{
                                fontWeight: "700",
                                color: "#0F172A",
                                // backgroundColor: "#F0F9FF",
                                padding: "2px 10px",
                                borderRadius: "4px",
                                fontSize: "12px"
                            }}>
                                {event.reassignmentData.updatedByUser}
                            </span>
                            {event.reassignmentData.updatedByUserLevel && (
                                <span style={{
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    backgroundColor: "#F8FAFC",
                                    color: "#1E40AF",
                                    padding: "2px 8px",
                                    borderRadius: "999px",
                                    border: "1px solid #E2E8F0"
                                }}>
                                    {event.reassignmentData.updatedByUserLevel}
                                </span>
                            )}
                        </div>

                        <ArrowRight size={14} color="#94A3B8" />

                        {/* Reassigned To */}
                        {event.reassignmentData.reassignmentUser && (
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <span style={{ fontSize: "12px", color: "#64748B" }}>Reassigned To:</span>
                                <span style={{
                                    fontWeight: "700",
                                    color: "#0F172A",
                                    // backgroundColor: "#F8FAFC",
                                    padding: "2px 10px",
                                    borderRadius: "4px",
                                    fontSize: "12px"
                                }}>
                                    {event.reassignmentData.reassignmentUser}
                                </span>
                                {event.reassignmentData.reassignmentUserLevel && (
                                    <span style={{
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        backgroundColor: "#F8FAFC",
                                        color: "#0F172A",
                                        padding: "2px 8px",
                                        borderRadius: "999px",
                                        border: "1px solid #E2E8F0"
                                    }}>
                                        {event.reassignmentData.reassignmentUserLevel}
                                    </span>
                                )}
                            </div>
                        )}
                    {/* ✅ SLA Paused At — Show when status is Issue Pending at User Level OR when originalSaveLevel is ISSUE_PENDING_AT_USER_LEVEL */}
                    {event.slaPausedAt && (event.current === "Issue Pending at User Level" || event.originalSaveLevel === "ISSUE_PENDING_AT_USER_LEVEL") && (
                        <>
                            <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <Clock size={14} color="#C2410C" />
                                <div style={{ fontSize: "13px", color: "#64748B" }}>SLA Paused At:</div>
                                <div style={{
                                    fontWeight: "700",
                                    color: "#9A3412",
                                    backgroundColor: "rgba(234, 88, 12, 0.1)",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    border: "1px solid #FED7AA"
                                }}>
                                    {event.slaPausedAt}
                                </div>
                            </div>
                        </>
                    )}
                    {/* ✅ SLA Resumed At — Show when status changes FROM "Issue Pending at User Level" AND slaPaused is false */}
                    {event.slaPaused === false &&
                        (event.previous?.includes("Pending") || event.originalSaveLevel?.includes("PENDING")) &&
                        event.statusChangeDateTime && (
                            <>
                                <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <Clock size={14} color="#059669" />
                                    <div style={{ fontSize: "13px", color: "#64748B" }}>SLA Resumed At:</div>
                                    <div style={{
                                        fontWeight: "700",
                                        color: "#065F46",
                                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                        border: "1px solid #A7F3D0"
                                    }}>
                                        {formatDateTime(event.statusChangeDateTime)}
                                    </div>
                                </div>
                            </>
                        )}
                </div>

            </div>
        )}
        {/* Status Change-specific content */}
        <StatusChangeDetails event={event} formatDateTime={formatDateTime} />

        {/* Resolution SLA Date & Time */}
        {/* {event.resolutionSLADateTime && (
            <>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "10px" }}>
                    <Calendar size={14} color="#1D4ED8" />
                    <div style={{ fontSize: "13px", color: "#64748B" }}>Resolution Date & Time:</div>
                    <div style={{
                        fontWeight: "700",
                        color: "#1E40AF",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "13px",
                        border: "1px solid #BFDBFE"
                    }}>
                        {formatDateTime(event.resolutionSLADateTime)}
                    </div>
                </div>
            </>
        )} */}

       
    </div>
);


const LevelAssignmentEvent = ({ event, formatDateTime }) => {
    const levelConfig = {
        L1: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE", badge: "#DBEAFE", badgeText: "#1E40AF" },
        L2: { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0", badge: "#D1FAE5", badgeText: "#065F46" },
        L3: { bg: "#FAF5FF", color: "#7C3AED", border: "#E9D5FF", badge: "#EDE9FE", badgeText: "#5B21B6" },
    };
    // eslint-disable-next-line no-unused-vars
    const cfg = levelConfig[event.level] || {
        bg: "#F8FAFC", color: "#475569", border: "#E2E8F0",
        badge: "#E2E8F0", badgeText: "#475569"
    };

    return (
        <div>
            {/* Event Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, flexWrap: "wrap" }}>
                    {/* Type Badge */}
                    <div style={{
                        padding: "6px 12px",
                        backgroundColor: "#F8FAFC",
                        color: "#64748B",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                    }}>
                        {/* {event.level}  */}
                        Assignment
                    </div>

                    {/* Grievance Status Badge */}
                    {event.grievanceStatus && (
                        <>
                            <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                            <span style={{
                                padding: "4px 10px",
                                backgroundColor: "#DBEAFE",
                                color: "#1E40AF",
                                borderRadius: "4px",
                                fontWeight: "600",
                                fontSize: "13px"
                            }}>
                                {event.grievanceStatus}
                            </span>
                        </>
                    )}
                </div>

                <div style={{
                    fontSize: "14px",
                    color: "#64748B",
                    fontWeight: "500",
                    whiteSpace: "nowrap"
                }}>
                    <Clock size={16} style={{ marginRight: "6px" }} />
                    {formatDateTime(event.timestamp)}
                </div>
            </div>

            {/* Reassigned By → Previous User → New User Row */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                padding: "8px 12px",
                backgroundColor: "#F8FAFC",
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
                marginBottom: "10px",
                justifyContent: "space-between"
            }}>
                {/* Assigned By */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "13px", color: "#64748B" }}>Performed By:</span>
                    <span style={{
                        fontWeight: "700",
                        color: "#0F172A",
                        // backgroundColor: "#F1F5F9",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "13px"
                    }}>
                        {event.reassignedBy}
                    </span>
                
                {/* Badge for L1_ASSIGNMENT or L2_ASSIGNMENT */}
                {event.eventType === "L1_ASSIGNMENT" && (
                    <span style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        // backgroundColor: "#FEF3C7",
                        color: "#0F172A",
                        padding: "2px 8px",
                        borderRadius: "999px",
                        border: "1px solid #E2E8F0",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                    }}>
                        HO
                    </span>
                )}

                {event.eventType === "L2_ASSIGNMENT" && (
                    <span style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        // backgroundColor: "#DBEAFE",
                        color: "#0F172A",
                        padding: "2px 8px",
                        borderRadius: "999px",
                        border: "1px solid #E2E8F0",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                    }}>
                        <UserCheck size={11} />
                        Support Team Admin
                    </span>
                )}
                </div>
                <ArrowRight size={14} color="#94A3B8" />

                {/* Previous User */}
                {event.previousUser && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "13px", color: "#64748B" }}>Previous User:</span>
                        <span style={{
                            fontWeight: "700",
                            color: "#0F172A",
                            // backgroundColor: "#FEE2E2",
                            padding: "2px 10px",
                            borderRadius: "4px",
                            fontSize: "13px"
                        }}>
                            {event.previousUser}
                        </span>
                        <span style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: "#F8FAFC",
                            color: "#0F172A",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            border: `1px solid #E2E8F0`
                        }}>
                            {event.level}
                        </span>
                    </div>
                )}

                <ArrowRight size={14} color="#94A3B8" />

                {/* New User */}
                {event.newUser && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "13px", color: "#64748B" }}>New User:</span>
                        <span style={{
                            fontWeight: "700",
                            color: "#0F172A",
                            backgroundColor: "#F8FAFC",
                            padding: "2px 10px",
                            borderRadius: "4px",
                            fontSize: "13px",
                            // border: `1px solid #E2E8F0`
                        }}>
                            {event.newUser}
                        </span>
                        <span style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: "#F8FAFC",
                            color: "#0F172A",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            border: `1px solid #E2E8F0`
                        }}>
                            {event.level}
                        </span>
                    </div>
                )}
            </div>

        </div>
    );
};

// Bulk Assignment Event Component
const BulkAssignmentEvent = ({ event, formatDateTime }) => {
    const levelConfig = {
        L1: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE", badge: "#DBEAFE", badgeText: "#1E40AF" },
        L2: { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0", badge: "#D1FAE5", badgeText: "#065F46" },
        L3: { bg: "#FAF5FF", color: "#7C3AED", border: "#E9D5FF", badge: "#EDE9FE", badgeText: "#5B21B6" },
    };

    const user = event.assignedUser;
    const cfg = levelConfig[user?.userLevel] || {
        bg: "#F8FAFC", color: "#475569", border: "#E2E8F0",
        badge: "#E2E8F0", badgeText: "#475569"
    };

    return (
        <div>
            {/* Event Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                    <div style={{
                        padding: "6px 12px",
                        backgroundColor: "#F8FAFC",
                        color: "#64748B",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                    }}>
                        Bulk Assignment
                    </div>
                    {event.isAdminAssignment && (
                        <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "3px 10px",
                            backgroundColor: "#F8FAFC",
                            color: "#0F172A",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: "700",
                            border: "1px solid #E2E8F0"
                        }}>
                            <UserCheck size={11} />
                            Support Team Admin
                        </span>
                    )}
                </div>
                <div style={{
                    fontSize: "14px",
                    color: "#64748B",
                    fontWeight: "500",
                    whiteSpace: "nowrap"
                }}>
                    <Clock size={16} style={{ marginRight: "6px" }} />
                    {formatDateTime(event.timestamp)}
                </div>
            </div>

            {/* Assigned By → Assigned To row */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                padding: "8px 12px",
                backgroundColor: "#F8FAFC",
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
                justifyContent: "space-between",
                marginBottom: "10px"
            }}>
                {/* Assigned By */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "13px", color: "#64748B" }}>Performed By:</span>
                    <span style={{
                        fontWeight: "700",
                        color: "#475569",
                        backgroundColor: "#F1F5F9",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "13px"
                    }}>
                        {event.assignedBy}
                    </span>
                </div>

                <ArrowRight size={14} color="#94A3B8" />

                {/* Assigned To */}
                {user && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "13px", color: "#64748B" }}>Assigned To:</span>
                        <span style={{
                            fontWeight: "700",
                            color: "#475569",
                            backgroundColor: "#F1F5F9",
                            padding: "2px 10px",
                            borderRadius: "4px",
                            fontSize: "13px"
                        }}>
                            {user.userName}
                        </span>
                        {user.userLevel && (
                            <span style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                backgroundColor: cfg.badge,
                                color: cfg.badgeText,
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: `1px solid ${cfg.border}`
                            }}>
                                {user.userLevel}
                            </span>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
};

// Reassignment Event Component
const ReassignmentEvent = ({ event, formatDateTime }) => {
    const levelConfig = {
        L1: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE", badge: "#DBEAFE", badgeText: "#1E40AF" },
        L2: { bg: "#F0FDF4", color: "#166534", border: "#BBF7D0", badge: "#D1FAE5", badgeText: "#065F46" },
        L3: { bg: "#FAF5FF", color: "#7C3AED", border: "#E9D5FF", badge: "#EDE9FE", badgeText: "#5B21B6" },
    };

    const toCfg = levelConfig[event.reassignmentUserLevel] || {
        bg: "#F8FAFC", color: "#475569", border: "#E2E8F0",
        badge: "#E2E8F0", badgeText: "#475569"
    };

    return (
        <div>
            {/* Event Header */}
            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "10px"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, flexWrap: "wrap" }}>
                    {/* Type Badge */}
                    <div style={{
                        padding: "6px 12px",
                        backgroundColor: `${event.color}15`,
                        color: event.color,
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                    }}>
                        {event.type}
                    </div>

                    {/* Grievance Status beside the label */}
                    {event.grievanceStatus && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                            <div style={{ width: "1px", height: "16px", backgroundColor: "#E2E8F0" }} />
                            <span style={{
                                padding: "4px 10px",
                                backgroundColor: "#DBEAFE",
                                color: "#1E40AF",
                                borderRadius: "4px",
                                fontWeight: "600",
                                fontSize: "13px"
                            }}>
                                {event.grievanceStatus}
                            </span>
                        </div>
                    )}
                </div>

                <div style={{
                    fontSize: "14px",
                    color: "#64748B",
                    fontWeight: "500",
                    whiteSpace: "nowrap"
                }}>
                    <Clock size={16} style={{ marginRight: "6px" }} />
                    {formatDateTime(event.timestamp)}
                </div>
            </div>

            {/* Performed By → Reassigned To row */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                padding: "8px 12px",
                backgroundColor: "#F8FAFC",
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
                justifyContent: "space-between",
                marginBottom: "10px"
            }}>
                {/* Performed By + Level */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "13px", color: "#64748B" }}>Performed By:</span>
                    <span style={{
                        fontWeight: "700",
                        color: "#475569",
                        backgroundColor: "#F1F5F9",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "13px"
                    }}>
                        {event.fromUser}
                    </span>
                    {event.fromLevel && (
                        <span style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: levelConfig[event.fromLevel]?.badge || "#E2E8F0",
                            color: levelConfig[event.fromLevel]?.badgeText || "#475569",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            border: `1px solid ${levelConfig[event.fromLevel]?.border || "#E2E8F0"}`
                        }}>
                            {event.fromLevel}
                        </span>
                    )}
                </div>

                <ArrowRight size={14} color="#94A3B8" />

                {/* Reassigned To + Level */}
                {event.reassignmentUser && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "13px", color: "#64748B" }}>Reassigned To:</span>
                        <span style={{
                            fontWeight: "700",
                            color: "#475569",
                            backgroundColor: "#F1F5F9",
                            padding: "2px 10px",
                            borderRadius: "4px",
                            fontSize: "13px"
                        }}>
                            {event.reassignmentUser}
                        </span>
                        {event.reassignmentUserLevel && (
                            <span style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                backgroundColor: toCfg.badge,
                                color: toCfg.badgeText,
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: `1px solid ${toCfg.border}`
                            }}>
                                {event.reassignmentUserLevel}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Reassignment Description */}
            {event.description && (
                <div style={{
                    backgroundColor: "#F0F9FF",
                    border: "1px solid #BAE6FD",
                    padding: "12px",
                    borderRadius: "6px"
                }}>
                    <div style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#0369A1",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <FileText size={12} />
                        Resolved Description
                    </div>
                    <div style={{
                        fontSize: "13px",
                        color: "#0C4A6E",
                        fontWeight: "500",
                        lineHeight: "1.4"
                    }}>
                        {event.description}
                    </div>
                </div>
            )}
        </div>
    );
};

// Escalation Event Component
const EscalationEvent = ({ event, formatDateTime }) => (
    <div>
        {/* Event Header */}
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                <div style={{
                    padding: "6px 12px",
                    backgroundColor: "#F8FAFC",
                    color: "#64748B",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                }}>
                    {event.escalationType === "Auto" ? "Auto Escalation" : "Manual Escalation"}
                </div>

                {/* Show escalation level */}
                {event.fromLevel && event.toLevel && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "14px"
                    }}>
                        <span style={{
                            padding: "4px 8px",
                            // backgroundColor: "#FEE2E2",
                            color: "#64748B",
                            borderRadius: "4px",
                            fontWeight: "600"
                        }}>
                            {event.fromLevel}
                        </span>
                        <ArrowRight size={16} color="#64748B" />
                        <span style={{
                            padding: "4px 8px",
                            // backgroundColor: "#FCE7F3",
                            color: "#64748B",
                            borderRadius: "4px",
                            fontWeight: "600"
                        }}>
                            {event.toLevel}
                        </span>
                    </div>
                )}
            </div>

            <div style={{
                fontSize: "14px",
                color: "#64748B",
                fontWeight: "500",
                whiteSpace: "nowrap"
            }}>
                <Clock size={16} style={{ marginRight: "6px" }} />
                {formatDateTime(event.timestamp)}
            </div>
        </div>

        {/* Escalation-specific content */}
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginTop: "12px"
        }}>
            {/* Escalation Details in Single Row */}
            {(event.performedBy || event.escalatedTo || event.escalationType) && (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    padding: "6px 10px",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "4px",
                    border: "1px solid #E2E8F0",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                    {/* Performed By */}
                    {event.performedBy && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                            <div style={{
                                fontSize: "12px",
                                color: "#64748B"
                            }}>
                                Performed By:
                            </div>
                            <div style={{
                                padding: "4px 8px",
                                // backgroundColor: "#F1F5F9",
                                color: "#0F172A",
                                borderRadius: "4px",
                                fontWeight: "600",
                                fontSize: "12px"
                            }}>
                                {event.performedBy}
                            </div>
                        </div>
                    )}

                    {/* Separator */}
                    {event.performedBy && event.escalatedTo && (
                        <div style={{
                            width: "1px",
                            height: "16px",
                            backgroundColor: "#E2E8F0"
                        }}></div>
                    )}

                    {/* Escalated To */}
                    {event.escalatedTo && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                            <div style={{
                                fontSize: "12px",
                                color: "#64748B"
                            }}>
                                Escalated To:
                            </div>
                            {event.escalatedToName && event.escalatedToName !== "N/A" && (
                            <span style={{
                                fontWeight: "700",
                                color: "#0F172A",
                                // backgroundColor: "#FEE2E2",
                                padding: "2px 10px",
                                borderRadius: "4px",
                                fontSize: "13px"
                            }}>
                                {event.escalatedToName}
                            </span>
                            )}
                            <span style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                backgroundColor: "#F8FAFC",
                                color: "#0F172A",
                                padding: "2px 8px",
                                borderRadius: "999px",
                                border: `1px solid #E2E8F0`
                            }}>
                                {event.escalatedTo}
                            </span>
                        </div>
                    )}

                    {/* Separator */}
                    {event.escalatedTo && event.escalationType && (
                        <div style={{
                            width: "1px",
                            height: "16px",
                            backgroundColor: "#E2E8F0"
                        }}></div>
                    )}

                    {/* Escalation Type */}
                    {event.escalationType && (
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                        }}>
                            <div style={{
                                fontSize: "12px",
                                color: "#64748B"
                            }}>
                                Type:
                            </div>
                            <div style={{
                                padding: "4px 8px",
                                // backgroundColor: event.escalationType === "Auto" ? "#DBEAFE" : "#FEF3C7",
                                color: "#0F172A",
                                borderRadius: "4px",
                                fontWeight: "600",
                                fontSize: "12px"
                            }}>
                                {event.escalationType}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Escalation Details, SLA Breached At, Trigger - Single Row */}
            {(event.details || event.slaBreachedAt || event.trigger) && (
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                    padding: "6px 10px",
                    backgroundColor: "#FDF2F8",
                    border: "1px solid #FBCFE8",
                    borderRadius: "4px",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                    {/* ✅ Escalation Reason — label + value inline */}
                    {event.details && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Activity size={14} color="#831843" />
                            <span style={{ fontSize: "12px", color: "#831843", fontWeight: "600" }}>
                                Escalation Reason:
                            </span>
                            <span style={{
                                fontSize: "12px",
                                color: "#831843",
                                fontWeight: "500",
                                backgroundColor: "rgba(234, 179, 8, 0.1)",
                                padding: "2px 8px",
                                borderRadius: "4px"
                            }}>
                                {event.details}
                            </span>
                        </div>
                    )}

                    {event.details && (event.slaBreachedAt || event.trigger) && (
                        <div style={{ width: "1px", height: "16px", backgroundColor: "#FBCFE8" }} />
                    )}

                    {/* SLA Breached At */}
                    {event.slaBreachedAt && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "12px", color: "#831843", fontWeight: "600" }}>
                                SLA Breached At:
                            </span>
                            <span style={{
                                fontSize: "12px", color: "#831843", fontWeight: "500",
                                backgroundColor: "rgba(234, 179, 8, 0.1)",
                                padding: "2px 8px", borderRadius: "4px"
                            }}>
                                {formatDateTime(event.slaBreachedAt)}
                            </span>
                        </div>
                    )}

                    {event.slaBreachedAt && event.trigger && (
                        <div style={{ width: "1px", height: "16px", backgroundColor: "#FBCFE8" }} />
                    )}

                    {/* Trigger */}
                    {event.trigger && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ fontSize: "12px", color: "#831843", fontWeight: "600" }}>
                                Trigger:
                            </span>
                            <span style={{
                                fontSize: "12px", color: "#831843", fontWeight: "500",
                                backgroundColor: "rgba(234, 179, 8, 0.1)",
                                padding: "2px 8px", borderRadius: "4px"
                            }}>
                                {event.trigger}
                            </span>
                        </div>
                    )}
                </div>
            )}

        </div>
    </div>
);

// SLA Pause Event Component
const SLAPauseEvent = ({ event, formatDateTime }) => (
    <div>
        {/* Event Header */}
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "16px"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                <div style={{
                    padding: "6px 12px",
                    backgroundColor: `${event.color}15`,
                    color: event.color,
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                }}>
                    {event.isPaused ? "SLA Paused" : "SLA Resumed"}
                </div>
            </div>

            <div style={{
                fontSize: "14px",
                color: "#64748B",
                fontWeight: "500",
                whiteSpace: "nowrap"
            }}>
                <Clock size={16} style={{ marginRight: "6px" }} />
                {formatDateTime(event.timestamp)}
            </div>
        </div>

        {/* SLA Pause-specific content */}
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            marginTop: "12px"
        }}>
            {/* Duration */}
            {event.duration && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "12px",
                    backgroundColor: "#F0F9FF",
                    borderRadius: "6px",
                    border: "1px solid #BAE6FD"
                }}>
                    <div style={{
                        fontSize: "12px",
                        color: "#0369A1",
                        fontWeight: "600"
                    }}>
                        Pause Duration:
                    </div>
                    <div style={{
                        fontSize: "12px",
                        color: "#0369A1",
                        fontWeight: "500",
                        backgroundColor: "rgba(6, 182, 212, 0.1)",
                        padding: "4px 8px",
                        borderRadius: "4px"
                    }}>
                        {event.duration}
                    </div>
                </div>
            )}

            {/* Reason */}
            {event.reason && (
                <div style={{
                    backgroundColor: "#F0F9FF",
                    border: "1px solid #BAE6FD",
                    padding: "12px",
                    borderRadius: "6px"
                }}>
                    <div style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#0369A1",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <FileText size={12} />
                        {event.isPaused ? "Pause Reason" : "Resume Reason"}
                    </div>
                    <div style={{
                        fontSize: "13px",
                        color: "#0C4A6E",
                        fontWeight: "500",
                        lineHeight: "1.4"
                    }}>
                        {event.reason}
                    </div>
                </div>
            )}

            {/* Paused/Resumed By */}
            <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                color: "#64748B"
            }}>
                <span>{event.isPaused ? "Paused By:" : "Resumed By:"}</span>
                <span style={{
                    fontWeight: "600",
                    color: "#475569",
                    backgroundColor: "#F1F5F9",
                    padding: "2px 8px",
                    borderRadius: "4px"
                }}>
                    {event.user}
                </span>
            </div>
        </div>
    </div>
);


// Event component mapper - Updated
const EventComponentMapper = ({ event, formatDateTime }) => {
    switch (event.type) {
        // case "Assignment":
        //     return <AssignmentEvent event={event} formatDateTime={formatDateTime} />;
        case "Status Change":
            return <StatusChangeEvent event={event} formatDateTime={formatDateTime} />;
        // case "Comment Added":
        //     return <CommentEvent event={event} formatDateTime={formatDateTime} />;
        // case "Attachment":
        //     return <AttachmentEvent event={event} formatDateTime={formatDateTime} />;
        case "Reassignment":
            return <ReassignmentEvent event={event} formatDateTime={formatDateTime} />;
        case "Escalation":
            return <EscalationEvent event={event} formatDateTime={formatDateTime} />;
        case "Bulk Assignment":
            return <BulkAssignmentEvent event={event} formatDateTime={formatDateTime} />;
        case "Level Assignment":
            return <LevelAssignmentEvent event={event} formatDateTime={formatDateTime} />;
        case "SLA Pause":
            return <SLAPauseEvent event={event} formatDateTime={formatDateTime} />;
        default:
            // return <GenericEvent event={event} formatDateTime={formatDateTime} />;
            return null;
    }
};
const DetailsAuditHistory = () => {
    const location = useLocation();
     // eslint-disable-next-line no-unused-vars
    const [selectedActionType, setSelectedActionType] = useState("All Actions");
    const [expandedSection, setExpandedSection] = useState({
        ticketCreation: true,
        auditTimeline: true
    });
    const [auditHistoryData, setauditHistoryData] = useState(null);
   // const [ticketSummary, setTicketSummary] = useState(null);
    const [auditEvents, setAuditEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

useEffect(() => {
    if (location.state) {
        setIsLoading(true);
        setauditHistoryData(location.state.auditHistoryData);
       // setTicketSummary(location.state.ticketSummary);
        console.log("Ticket Data received:", location.state.auditHistoryData);
        if (location.state.auditHistoryData) {
            const events = extractAuditEvents(location.state.auditHistoryData);
            setAuditEvents(events);
        }
        setIsLoading(false);
    } else {
        setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.state]);

const extractAuditEvents = (auditData) => {
    const events = [];

    if (!auditData || !auditData.auditHistory) {
        return events;
    }

    const { auditHistory, grievanceSeqId} = auditData;

    // First pass: collect all events
    auditHistory.forEach((historyItem, index) => {
        const { eventType, event, timestamp, performedBy, details} = historyItem;

        switch (eventType) {
            case "TICKET_CREATION":
                events.push({
                    type: "Ticket Creation",
                    timestamp: timestamp,
                    user: details.reportedBy || performedBy || "System",
                    details: `Ticket #${grievanceSeqId} was created`,
                    description: details.description || "",
                    priority: details.priority,
                    category: details.category,
                    subcategory: details.subcategory,
                    escalationLevel: details.escalationLevel,
                    resolutionName: details.preferredResolutionMethod,
                    ticketDateTime: details.ticketCreatedDateTime,
                    ticketResolvedDateTime: details.resolutionDateTime,
                    phoneNumber: details.requestorContactNumber,
                    email: details.requestorEmail,
                    bankName: details.bankName,
                    branchName: details.branchName,
                    assignedTeamName: details.supportTeam,
                    subject: details.subject,
                    createdBy: details.requestorUserId,
                    createdByName: details.reportedBy,
                    icon: <User size={16} />,
                    color: "#8B5CF6",
                    isCreation: true
                });
                break;

            case "STATUS_CHANGE":
                const previousStatus = details.oldStatus || getStatusFromId(details.oldStatusId);
                const currentStatus = details.newStatus || getStatusFromId(details.newStatusId);

                // Check if there's a matching REASSIGNMENT event
                let reassignmentData = null;
                const nextEvent = auditHistory[index + 1];
                if (nextEvent && nextEvent.eventType === "REASSIGNMENT" && 
                    nextEvent.details.grievanceStatus === currentStatus) {
                    reassignmentData = {
                        updatedByUser: nextEvent.details.updatedByUser,
                        updatedByUserLevel: nextEvent.details.updatedByUserLevel,
                        reassignmentUser: nextEvent.details.reassignmentUser,
                        reassignmentUserLevel: nextEvent.details.reassignmentUserLevel,
                        reassignmentDateTime: nextEvent.details.reassignmentDateTime,
                        reassignmentDescription: nextEvent.details.reassignmentDescription
                    };
                }

                events.push({
                    type: "Status Change",
                    timestamp: timestamp,
                    user: details.actionTakenBy || details.performedBy || performedBy || "System",
                    previous: previousStatus,
                    current: currentStatus,
                    userLevel: details.performedByUserLevel,
                    handledLevel: details.handledLevel,
                    resolutionSLADateTime: details.resolutionDateTime,
                    rootCause: details.rootCause,
                    escalateRemarks: details.escalateRemarks,
                    actionMessage: details.actionMessage,
                    resolutionSteps: details.resolutionSteps,
                    reassignmentDescription: details.reassignmentDescription,
                    slaPausedAt: details.slaPausedAt,
                    slaPaused: details.slaPaused,
                    statusChangeDateTime: details.statusChangeDateTime,
                    originalSaveLevel: historyItem.originalSaveLevel,
                    reassignmentData: reassignmentData, // Add this
                    comments: [],
                    icon: <ArrowRightLeft size={16} />,
                    color: "#8B5CF6"
                });
                break;

            case "ASSIGNMENT":
                events.push({
                    type: "Assignment",
                    timestamp: timestamp,
                    user: performedBy || "System",
                    details: `Assigned to ${details.assignedTo}`,
                    assignedTo: details.assignedTo,
                    teamName: details.teamName,
                    icon: <Users size={16} />,
                    color: "#10B981"
                });
                break;

            case "REASSIGNMENT":
                // Check if this reassignment was already merged with a status change
                const prevEvent = auditHistory[index - 1];
                if (prevEvent && prevEvent.eventType === "STATUS_CHANGE" && 
                    prevEvent.details.newStatus === details.grievanceStatus) {
                    // Skip this event as it's already merged
                    break;
                }
                // Otherwise, create a separate reassignment event (existing code)
                events.push({
                    type: "Reassignment",
                    timestamp: timestamp,
                    user: details.updatedByUser || performedBy || "System",
                    fromUser: details.updatedByUser || performedBy,
                    fromLevel: details.updatedByUserLevel,
                    toUser: details.reassignmentUser,
                    toLevel: details.reassignmentUserLevel,
                    reassignmentUser: details.reassignmentUser,
                    reassignmentUserLevel: details.reassignmentUserLevel,
                    updatedByUserLevel: details.updatedByUserLevel,
                    grievanceStatus: details.grievanceStatus,
                    description: details.reassignmentDescription,
                    icon: <UserCheck size={16} />,
                    color: "#8B5CF6"
                });
                break;

            case "ESCALATION":
                events.push({
                    type: "Escalation",
                    timestamp: timestamp,
                    user: performedBy || details.performedBy || "System",
                    details: details.escalationType === "Auto"
                        ? (details.escalationReason || "Ticket auto-escalated due to SLA breach")
                        : "",
                    escalationType: details.escalationType,
                    performedBy: details.performedBy || performedBy,
                    escalatedToName: details.escalatedToName,
                    escalatedTo: details.escalatedTo,
                    escalationRemarks: details.escalationRemarks,
                    fromLevel: details.escalatedFrom,
                    toLevel: details.escalatedTo,
                    reason: details.reason,
                    slaBreachedAt: details.slaBreachedAt,
                    trigger: details.trigger,
                    icon: <ArrowRight size={16} />,
                    color: "#8B5CF6"
                });
                break;

            case "GENERIC_UPDATE":
                // Check if this is an auto-escalate event
                if (details.saveLevel === "AUTO-ESCALATE" || event.includes("AUTO-ESCALATE")) {
                    events.push({
                        type: "Escalation",
                        timestamp: timestamp,
                        user: details.updatedBy || performedBy || "System",
                        details: details.escalationReason || "Ticket auto-escalated due to SLA breach",
                        escalationType: "Auto",
                        performedBy: details.updatedBy || performedBy || "System",
                        escalatedTo: details.escalatedTo,
                        fromLevel: details.fromLevel,
                        toLevel: details.toLevel,
                        trigger: "SLA Breach",
                        slaBreachedAt: details.slaBreachedAt || details.updateDateTime,
                        icon: <ArrowRight size={16} />,
                        color: "#8B5CF6"
                    });
                } 
                
                break;

            case "BULK_ASSIGNMENT":
                events.push({
                    type: "Bulk Assignment",
                    timestamp: timestamp,
                    user: details.assignedBy || performedBy || "System",
                    assignedBy: details.assignedBy || performedBy,
                    isAdminAssignment: details.isAdminAssignment || false,
                    assignedByAdminUserId: details.assignedByAdminUserId,
                    assignedUser: details.assignedUser || null,
                    assignmentDateTime: details.assignmentDateTime,
                    icon: <Users size={16} />,
                    color: "#8B5CF6"
                });
                break;

            case "L1_ASSIGNMENT":
            case "L2_ASSIGNMENT": {
                const level = eventType === "L1_ASSIGNMENT" ? "L1" : "L2";
                const previousUser = details.previousL1User || details.previousL2User;
                const newUser = details.newL1User || details.newL2User;

                events.push({
                    type: "Level Assignment",
                    timestamp: timestamp,
                    user: performedBy || "System",
                    level: level,
                    reassignedBy: details.reassignedBy || performedBy,
                    previousUser: previousUser,
                    newUser: newUser,
                    grievanceStatus: details.grievanceStatus,
                    reassignmentDescription: details.reassignmentDescription,
                    reassignmentDateTime: details.reassignmentDateTime,
                    eventType: eventType,
                    icon: <UserCheck size={16} />,
                    color: "#8B5CF6"
                });
                break;
            }

            case "SLA_PAUSE":
                events.push({
                    type: "SLA Pause",
                    timestamp: timestamp,
                    user: performedBy || "System",
                    details: details.isPaused ? "SLA paused" : "SLA resumed",
                    isPaused: details.isPaused,
                    reason: details.reason,
                    duration: details.duration,
                    icon: <Clock size={16} />,
                    color: "#F97316"
                });
                break;

            default:
                
                break;
        }
    });

    // return events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return events;
};

    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";

        let date;
        try {
            // Check if the date is already in DD-MM-YYYY format
            if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}/)) {
                const [day, month, year] = dateString.split(' ')[0].split('-');
                const timePart = dateString.split(' ')[1] || '';
                const ampm = dateString.split(' ')[2] || '';
                date = new Date(`${year}-${month}-${day}T${timePart} ${ampm}`);
            } else {
                date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    date = new Date(dateString.replace(/\s+/g, 'T').replace(/\./g, '-'));
                }
            }
        } catch (error) {
            return dateString || "Invalid Date";
        }

        if (isNaN(date)) return dateString || "N/A";

        const dd = String(date.getDate()).padStart(2, "0");
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const yyyy = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        const formattedHours = String(hours).padStart(2, "0");

        return `${dd}-${mm}-${yyyy} ${formattedHours}:${minutes} ${ampm}`;
    };

    const getStatusFromId = (statusId) => {
        const statusMap = {
            837: "Open",
            838: "In Progress",
            839: "Hold",
            840: "Resolved",
            841: "Closed",
            842: "Rejected",
            845: "Escalated to L2",
        };
        return statusMap[statusId] || "Open";
    };

    const filterAuditEvents = (events) => {
        if (selectedActionType === "All Actions") return events;
        return events.filter(event => event.type === selectedActionType);
    };
    // eslint-disable-next-line no-unused-vars
    const toggleSection = (section) => {
        setExpandedSection(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    if (isLoading) {
        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                backgroundColor: "#F8FAFC"
            }}>
                <Loader show={true} type="body" stack="vertical" message="Loading Audit Details" />
            </div>
        );
    }

    const filteredEvents = filterAuditEvents(auditEvents);
    const creationEvent = filteredEvents.find(event => event.isCreation);
    const timelineEvents = filteredEvents.filter(event => !event.isCreation);

    return (
        <div style={{
            backgroundColor: "#F8FAFC",
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            // minHeight: "100vh"
        }}>
            {/* Main Content */}
            <div style={{margin: "0 auto"}}>
            {/* <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px" }}> */}
                {/* Single Column Layout for Ticket Creation and Audit Timeline */}
                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                    {/* Section 1: Ticket Creation */}
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "6px",
                        overflow: "hidden",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                        // border: "1px solid #E2E8F0"
                    }}>
                        {/* Section Content */}
                        {/* Section Content */}
{expandedSection.ticketCreation && creationEvent && (
    <div style={{ padding: "10px" }}>
        {/* Timeline container with vertical line */}
        <div style={{ position: "relative", paddingLeft: "40px" }}>
            {/* Timeline vertical line - extends through ticket creation */}
            <div style={{
                position: "absolute",
                left: "19px",
                top: "12px",
                bottom: timelineEvents.length > 0 ? "-24px" : "12px",
                width: "2px",
                background: "linear-gradient(180deg, #CBD5E1 0%, #E2E8F0 100%)"
            }}></div>

            {/* Timeline node for ticket creation */}
            <div style={{
                position: "absolute",
                left: "0px",
                top: "12px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: creationEvent.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: `0 0 0 4px #F8FAFC, 0 0 0 6px ${creationEvent.color}30`,
                zIndex: 1
            }}>
                {creationEvent.icon}
            </div>

            {/* Event card */}
            <div style={{
                backgroundColor: "white",
                padding: "10px",
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                borderLeft: `4px solid ${creationEvent.color}`,
                boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)",
                transition: "all 0.2s ease",
                position: "relative"
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
                e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 4px 0 rgba(0, 0, 0, 0.05)";
                e.currentTarget.style.transform = "translateY(0)";
            }}>
                
                {/* Event Header - Same as Status Change */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "20px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
                        <div style={{
                            padding: "6px 12px",
                            backgroundColor: `#F8FAFC`,
                            color: `#64748B`,
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "700",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em"
                        }}>
                            Ticket Creation
                        </div>
                    </div>
                    
                    <div style={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <Clock size={16} />
                        {formatDateTime(creationEvent.timestamp)}
                    </div>
                </div>

                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(3, 1fr)",
                                        gap: "8px"
                                    }}>
                                        {/* Ticket No */}
                                        <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em"
                                            }}>
                                                Ticket ID
                                            </div>
                                            <div style={{
                                                fontSize: "14px",
                                                color: "#0F172A",
                                                fontWeight: "700",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px"
                                            }}>
                                                <FileText size={16} color="#3B82F6" />
                                                {auditHistoryData?.grievanceSeqId || "N/A"}
                                            </div>
                                        </div>

                                        {/* Bank Name */}
                                        <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em"
                                            }}>
                                                Bank Name
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#0F172A",
                                                fontWeight: "600"
                                            }}>
                                                {auditHistoryData?.auditHistory?.[0]?.details?.bankName || "N/A"}
                                            </div>
                                        </div>

                                        {/* Branch Name */}
                                        <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em"
                                            }}>
                                                Branch Name
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#0F172A",
                                                fontWeight: "600"
                                            }}>
                                                {auditHistoryData?.auditHistory?.[0]?.details?.branchName || "N/A"}
                                            </div>
                                        </div>

                                        {/* Priority */}
                                        <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em"
                                            }}>
                                                Priority
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#0F172A",
                                                fontWeight: "700",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px"
                                            }}>
                                                <Flag size={16} color={
                                                    auditHistoryData?.auditHistory?.[0]?.details?.priority === "Critical" ? "#EF4444" :
                                                        auditHistoryData?.auditHistory?.[0]?.details?.priority === "Major" ? "#FD7E14" :
                                                            auditHistoryData?.auditHistory?.[0]?.details?.priority === "Significant" ? "#FFC107" : "#64748B"
                                                } />
                                                {auditHistoryData?.auditHistory?.[0]?.details?.priority || "N/A"}
                                            </div>
                                        </div>

                                        {/* Ticket Created Date & Time */}
                                        {/* <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}>
                                                <Calendar size={12} />
                                                Ticket Created Date & Time
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#0F172A",
                                                fontWeight: "600"
                                            }}>
                                                {auditHistoryData?.auditHistory?.[0]?.details?.ticketCreatedDateTime || "N/A"}
                                            </div>
                                        </div> */}
                                        {/* Resolution Date & Time */}
                                        <div style={{
                                                padding: "10px",
                                                backgroundColor: "#F8FAFC",
                                                borderRadius: "8px",
                                                border: "1px solid #E2E8F0"
                                            }}>
                                                <div style={{
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    color: "#64748B",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }}>
                                                    <Calendar size={12} />
                                                    Resolution Date & Time
                                                </div>
                                                <div style={{
                                                    fontSize: "13px",
                                                    color: "#0F172A",
                                                    fontWeight: "600"
                                                }}>
                                                    {(() => {
                                                        // Find the last valid resolutionDateTime
                                                        const auditHistory = auditHistoryData?.auditHistory || [];

                                                        // Search from last to first object
                                                        for (let i = auditHistory.length - 1; i >= 0; i--) {
                                                            const item = auditHistory[i];

                                                            // Check if eventType is STATUS_CHANGE or TICKET_CREATION and resolutionDateTime is valid
                                                            if ((item.eventType === "STATUS_CHANGE" || item.eventType === "TICKET_CREATION") &&
                                                                item.details?.resolutionDateTime &&
                                                                item.details.resolutionDateTime !== "N/A" &&
                                                                item.details.resolutionDateTime !== null) {
                                                                return (
                                                                    <>
                                                                        {item.details.resolutionDateTime}
                                                                    </>
                                                                );
                                                            }
                                                        }

                                                        // Don't display anything if no valid resolution date found
                                                        return null;
                                                    })()}
                                                </div>
                                            </div>
                                        {/* Initial Resolution SLA Date & Time */}
                                        {creationEvent.initialResolutionSLADateTime && (
                                            <div style={{
                                                padding: "10px",
                                                backgroundColor: "#F8FAFC",
                                                borderRadius: "8px",
                                                border: "1px solid #E2E8F0"
                                            }}>
                                                <div style={{
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    color: "#64748B",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }}>
                                                    <Clock size={12} />
                                                    Initial Resolution SLA
                                                </div>
                                                <div style={{
                                                    fontSize: "13px",
                                                    color: "#0F172A",
                                                    fontWeight: "700",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}>
                                                    <Clock size={16} color="#3B82F6" />
                                                    {formatDateTime(creationEvent.initialResolutionSLADateTime)}
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                    
                                    {/* Section 3: Category & Support */}
                                    <div style={{
                                        marginTop: "10px",
                                        // marginBottom: "14px",
                                        paddingBottom: "14px",
                                        // borderBottom: "1px solid #F1F5F9"
                                    }}>
                                        <div style={{
                                            fontSize: "14px",
                                            fontWeight: "700",
                                            color: "#475569",
                                            marginBottom: "16px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px"
                                        }}>
                                            <div style={{
                                                width: "4px",
                                                height: "16px",
                                                backgroundColor: "#8B5CF6",
                                                borderRadius: "2px"
                                            }}></div>
                                            Category & Support Team Details
                                        </div>

                                        <div style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(3, 1fr)",
                                            gap: "8px"
                                        }}>
                                            {/* Category */}
                                            <div style={{
                                                padding: "10px",
                                                backgroundColor: "#f8fafc",
                                                borderRadius: "8px",
                                                border: "1px solid #E9D5FF"
                                            }}>
                                                <div style={{
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    color: "#64748b",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }}>
                                                    <Layers size={12} />
                                                    Category
                                                </div>
                                                <div style={{
                                                    fontSize: "13px",
                                                    color: "#0f172a",
                                                    fontWeight: "700"
                                                }}>
                                                    {auditHistoryData?.auditHistory?.[0]?.details?.category || "N/A"}
                                                </div>
                                            </div>

                                            {/* Subcategory */}
                                            <div style={{
                                                padding: "10px",
                                                backgroundColor: "#f8fafc",
                                                borderRadius: "8px",
                                                border: "1px solid #E9D5FF"
                                            }}>
                                                <div style={{
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    color: "#64748b",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }}>
                                                    <Layers size={12} />
                                                    Subcategory
                                                </div>
                                                <div style={{
                                                    fontSize: "13px",
                                                    color: "#0f172a",
                                                    fontWeight: "600"
                                                }}>
                                                    {auditHistoryData?.auditHistory?.[0]?.details?.subcategory || "N/A"}
                                                </div>
                                            </div>

                                            {/* Support Team */}
                                            <div style={{
                                                padding: "10px",
                                                backgroundColor: "#f8fafc",
                                                borderRadius: "8px",
                                                border: "1px solid #E9D5FF"
                                            }}>
                                                <div style={{
                                                    fontSize: "11px",
                                                    fontWeight: "700",
                                                    color: "#64748b",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.05em",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "4px"
                                                }}>
                                                    <Users size={12} />
                                                    Support Team
                                                </div>
                                                <div style={{
                                                    fontSize: "13px",
                                                    color: "#0f172a",
                                                    fontWeight: "600"
                                                }}>
                                                    {auditHistoryData?.auditHistory?.[0]?.details?.supportTeam || "N/A"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Section 4: Assignment & Subject */}
                                    <div style={{
                                        // marginBottom: "14px",
                                        // paddingBottom: "14px",
                                    }}>
                                        {/* Subject */}

                                        <div style={{
                                            padding: "10px",
                                            backgroundColor: "#F8FAFC",
                                            borderRadius: "8px",
                                            border: "1px solid #E2E8F0"
                                        }}>
                                            <div style={{
                                                fontSize: "11px",
                                                fontWeight: "700",
                                                color: "#64748B",
                                                marginBottom: "6px",
                                                textTransform: "uppercase",
                                                letterSpacing: "0.05em",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px"
                                            }}>
                                                <MessageSquare size={12} />
                                                Subject
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#0F172A",
                                                fontWeight: "700",
                                                lineHeight: "1.4"
                                            }}>
                                                {auditHistoryData?.auditHistory?.[0]?.details?.subject || "N/A"}
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                </div>
                                {/* Section Content */}
                        {expandedSection.auditTimeline && (
                            <div style={{ padding: "20px", paddingLeft: "0px" }}>
                                {timelineEvents.length === 0 ? (
                                    <></>
                                ) : (
                                    <div style={{ position: "relative", paddingLeft: "40px" }}>
                                        {/* Timeline vertical line */}
                                        <div style={{
                                            position: "absolute",
                                            left: "19px",
                                            top: "12px",
                                            bottom: "12px",
                                            width: "2px",
                                            background: "linear-gradient(180deg, #CBD5E1 0%, #E2E8F0 100%)"
                                        }}></div>

                                        {timelineEvents.map((event, index, array) => (
                                            <div
                                                key={index}
                                                style={{
                                                    marginBottom: index === array.length - 1 ? 0 : "10px",
                                                    position: "relative"
                                                }}
                                            >
                                                {/* Timeline node */}
                                                <div style={{
                                                    position: "absolute",
                                                    left: "-40px",
                                                    top: "12px",
                                                    width: "40px",
                                                    height: "40px",
                                                    borderRadius: "50%",
                                                    backgroundColor: event.color,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "white",
                                                    boxShadow: `0 0 0 4px #F8FAFC, 0 0 0 6px ${event.color}30`,
                                                    zIndex: 1
                                                }}>
                                                    {event.icon}
                                                </div>

                                                {/* Event card */}
                                                <div style={{
                                                    backgroundColor: "white",
                                                    padding: "13px",
                                                    borderRadius: "10px",
                                                    border: "1px solid #E2E8F0",
                                                    borderLeft: `4px solid ${event.color}`,
                                                    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)",
                                                    transition: "all 0.2s ease"
                                                }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
                                                        e.currentTarget.style.transform = "translateY(-2px)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.boxShadow = "0 2px 4px 0 rgba(0, 0, 0, 0.05)";
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                    }}>

                                                    {/* Use the appropriate component for each event type */}
                                                    <EventComponentMapper
                                                        event={event}
                                                        formatDateTime={formatDateTime}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                            </div>
                        )}
                    

                        
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DetailsAuditHistory;